import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { useCustomAppClient } from 'api/custom-client';
import {
    subscribeToCopilotActivity,
    type CopilotActivity
} from 'app/modules/wallforming/components/copilot/hub/copilotHub';
import { useCopilotHub } from 'app/modules/wallforming/components/copilot/hub/useCopilotHub';
import { getProposalContextSignature, isRecord } from 'app/modules/wallforming/components/copilot/utils/copilotUtils';

import type {
    CopilotChatRequest,
    CopilotChatResponse,
    CopilotChatState,
    CopilotConversation,
    CopilotLiveActivity
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';

const MAX_LIVE_REASONING_CHARACTERS = 4_000;

const applyActivity = (current: CopilotLiveActivity, activity: CopilotActivity): CopilotLiveActivity => {
    switch (activity.kind) {
        case 'status':
            return { ...current, status: activity.text };
        case 'reasoning':
            return { ...current, reasoning: (current.reasoning + activity.text).slice(-MAX_LIVE_REASONING_CHARACTERS) };
        case 'answer':
            return { ...current, answer: current.answer + activity.text };
        case 'tool':
            return { ...current, tools: [...current.tools, activity.text] };
        default:
            return current;
    }
};

interface UseCopilotMessageSenderProps {
    activeConversation: CopilotConversation | undefined;
    buildRequest: (prompt: string) => CopilotChatRequest | null;
    isOpen: boolean;
    isSending: boolean;
    prompt: string;
    requestOptions: { model?: string; timeoutSeconds: number };
    settingsValid: boolean;
    setChatState: Dispatch<SetStateAction<CopilotChatState>>;
    setError: Dispatch<SetStateAction<string | null>>;
    setIsSending: Dispatch<SetStateAction<boolean>>;
    setPrompt: Dispatch<SetStateAction<string>>;
}

export const useCopilotMessageSender = ({
    activeConversation,
    buildRequest,
    isOpen,
    isSending,
    prompt,
    requestOptions,
    settingsValid,
    setChatState,
    setError,
    setIsSending,
    setPrompt
}: UseCopilotMessageSenderProps) => {
    const { t } = useTranslation();
    const postChatTurn = useCustomAppClient<CopilotChatResponse>();
    const cancelOperation = useCustomAppClient<void>();
    const abortControllerRef = useRef<AbortController | null>(null);
    const operationIdRef = useRef<string | null>(null);
    const [liveActivity, setLiveActivity] = useState<CopilotLiveActivity | null>(null);
    useCopilotHub();

    useEffect(
        () =>
            subscribeToCopilotActivity((activity) => {
                if (activity.operationId === operationIdRef.current) {
                    setLiveActivity((current) => (current ? applyActivity(current, activity) : current));
                }
            }),
        []
    );

    // Aborting only the HTTP request may not stop the server behind proxies, so the operation is cancelled explicitly.
    const cancelMessage = useCallback(() => {
        const operationId = operationIdRef.current;
        abortControllerRef.current?.abort();
        if (operationId) {
            void cancelOperation({ url: `/operations/${operationId}`, method: 'DELETE' }).catch(() => undefined);
        }
    }, [cancelOperation]);

    useEffect(() => {
        if (!isOpen) {
            cancelMessage();
        }
    }, [cancelMessage, isOpen]);

    const sendMessage = useCallback(async () => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || isSending || !activeConversation || !settingsValid) {
            return;
        }

        const conversationId = activeConversation.id;
        let request: CopilotChatRequest | null;
        try {
            request = buildRequest(trimmedPrompt);
        } catch {
            setError(t('copilotContextBuildError'));
            return;
        }
        if (!request) {
            setError(t('copilotSelectContextError'));
            return;
        }

        const userMessage = { role: 'user' as const, content: trimmedPrompt };
        setChatState((current) => ({
            ...current,
            conversations: current.conversations.map((conversation) =>
                conversation.id === conversationId
                    ? {
                          ...conversation,
                          title:
                              conversation.messages.length === 0
                                  ? trimmedPrompt.length > 36
                                      ? `${trimmedPrompt.slice(0, 36)}...`
                                      : trimmedPrompt
                                  : conversation.title,
                          messages: [...conversation.messages, userMessage]
                      }
                    : conversation
            )
        }));
        setPrompt('');
        setError(null);
        setIsSending(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const operationId = uuidv4();
        operationIdRef.current = operationId;
        setLiveActivity({ status: '', reasoning: '', answer: '', tools: [] });

        try {
            const response = await postChatTurn({
                url: '/copilot/chat',
                method: 'POST',
                headers: { 'X-Operation-Id': operationId },
                data: { ...request, ...requestOptions },
                timeout: (requestOptions.timeoutSeconds + 30) * 1000,
                signal: abortController.signal
            });
            const contextSignature = getProposalContextSignature(request.context);
            setChatState((current) => ({
                ...current,
                conversations: current.conversations.map((conversation) =>
                    conversation.id === conversationId
                        ? {
                              ...conversation,
                              messages: [
                                  ...conversation.messages,
                                  {
                                      role: 'assistant',
                                      content: response.message,
                                      ...(response.tokenUsage ? { tokenUsage: response.tokenUsage } : {}),
                                      proposals: (response.proposals ?? []).map((proposal) => ({
                                          ...proposal,
                                          id: uuidv4(),
                                          contextSignature
                                      }))
                                  }
                              ]
                          }
                        : conversation
                )
            }));
        } catch (requestError: unknown) {
            if (!abortController.signal.aborted) {
                const response = isAxiosError<unknown>(requestError) ? requestError.response : undefined;
                const problem = response?.data;
                const detail = isRecord(problem) && typeof problem.detail === 'string' ? problem.detail.trim() : '';
                setError(detail || t('copilotRequestError'));
                setPrompt((current) => current || trimmedPrompt);
                setChatState((current) => ({
                    ...current,
                    conversations: current.conversations.map((conversation) =>
                        conversation.id === conversationId
                            ? {
                                  ...conversation,
                                  messages: conversation.messages.filter((message) => message !== userMessage)
                              }
                            : conversation
                    )
                }));
            } else {
                setChatState((current) => ({
                    ...current,
                    conversations: current.conversations.map((conversation) => {
                        const lastMessage = conversation.messages[conversation.messages.length - 1];
                        return conversation.id === conversationId &&
                            lastMessage?.role === 'user' &&
                            lastMessage.content === trimmedPrompt
                            ? { ...conversation, messages: conversation.messages.slice(0, -1) }
                            : conversation;
                    })
                }));
            }
        } finally {
            abortControllerRef.current = null;
            operationIdRef.current = null;
            setLiveActivity(null);
            setIsSending(false);
        }
    }, [
        activeConversation,
        buildRequest,
        isSending,
        postChatTurn,
        prompt,
        requestOptions,
        settingsValid,
        setChatState,
        setError,
        setIsSending,
        setPrompt,
        t
    ]);

    return { cancelMessage, liveActivity, sendMessage };
};
